import React, { useCallback, useState, useContext, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { withRouter } from 'react-router'

import xor from 'lodash/xor'

import Button from '@eig-builder/core-ui/Button'
import Checkbox from '@eig-builder/core-ui/Checkbox'
import FormControlLabel from '@eig-builder/core-ui/FormControlLabel'
import TextField from '@eig-builder/core-ui/TextField'

import { DataElementLocations } from '@eig-builder/core-utils/helpers/tagging-helper'

import ButtonWithLoader from 'core/components/button-with-loader'
import Modal from 'core/components/modal'

import RefundInformation from '../refund'
import { RefundContext } from '../refund/context'
import { DIRECTLY } from '../services-tab/constants'

import { resetCancelAccount, postRefundServices } from '../../../store/actions'
import { refreshContent } from 'modules/crm/helpers/refresh-content-helper'

import Text from '@eig-builder/module-localization'

const filterRefundableServices = refundableServices => {
  return (
    refundableServices &&
    refundableServices.map(item => {
      return {
        planName: item.sku,
        renewDate: item.renew_date,
        proRatedAmount: item.prorated_amount,
        billingServiceId: item.billing_service_id,
        term: item.term,
        refundOrCreditAmount: item.refund_or_credit_amount,
        initialDomainRegistrationFee: item.initial_domain_registration_fee,
        serviceId: item.id
      }
    })
  )
}

const CancelAccountDialog = props => {
  const { account, pending, successLogs, location } = props
  const { partnerId, id } = account
  const dispatch = useDispatch()
  const { refundType } = useContext(RefundContext)

  const [blackList, setBlackList] = useState(false)
  const [reason, setReason] = useState('')
  const [skipDomainRegistrationFreePlanIds, setIds] = useState([])

  const refundableServices = useSelector(
    state => state.crm.getRefundableServicesResponse && state.crm.getRefundableServicesResponse.refundable_services
  )

  const posting = useSelector(state => state.crm.postRefundServicesRetrieving)
  const postingSuccess = useSelector(state => state.crm.postRefundServicesSuccess)

  const refundServices = filterRefundableServices(refundableServices)

  const handleCheckbox = event => {
    setBlackList(event.target.checked)
  }

  const handleReasonChange = event => {
    setReason(event.target.value)
  }

  const cancelAccount = useCallback((accountId, partnerId) => {
    props.cancelAccountById(
      {
        black_list: blackList,
        reason
      },
      partnerId,
      accountId
    )
  })

  useEffect(() => {
    if (!posting && postingSuccess && refundType === '0') {
      cancelAccount(id, partnerId)
    }
  }, [posting, postingSuccess])

  const handleSubmit = () => {
    if (refundType === '0') {
      // Refund services can not be an empty list or be refunded without a pro rated amount
      const refundList = refundServices
        .filter(item => item.refundOrCreditAmount > 0)
        .map(i => parseInt(i.billingServiceId))
      if (refundList.length > 0 && refundType !== '-1') {
        dispatch(postRefundServices(refundList, refundType, id, partnerId, skipDomainRegistrationFreePlanIds))
      } else {
        cancelAccount(id, partnerId)
      }
    } else {
      cancelAccount(id, partnerId)
    }
  }

  const handleClose = () => {
    refreshContent(location, account, dispatch)
    dispatch(resetCancelAccount())
    props.handleDialog()
  }

  const cancelLogs = successLogs && successLogs.trim().split('\n')

  const actions = cancelLogs && !posting
    ? [
      <Button
        key='cancel-account-close'
        onClick={handleClose}
        dataElementLocation={DataElementLocations.BODY}
        dataElementLabel='cst-cancel-account--close'
        dataElementId='cst-cancel-account--close'
      >
        <Text message='modules.crm.components.account.close' />
      </Button>
    ]
    : [
      <Button
        key='cancel-account-cancel'
        color='secondary'
        disabled={pending || posting}
        onClick={props.handleDialog}
        dataElementLocation={DataElementLocations.BODY}
        dataElementLabel='cst-cancel-account--cancel'
        dataElementId='cst-cancel-account--cancel'
      >
        <Text message='modules.crm.components.account.cancel' />
      </Button>,
      <ButtonWithLoader
        key='cancel-account-submit'
        disabled={!reason || pending || posting}
        onClick={handleSubmit}
        dataElementSuffix='cancel-account--submit'
        showLoader={pending || posting}
        text={<Text message='modules.crm.components.account.submit' />}
      />
    ]

  return (
    <Modal
      actions={actions}
      dataElementPrefix='cst-account--cancel-account'
      disableBackdropClick
      handleClose={props.handleDialog}
      open
      title={<Text message='modules.crm.components.account.cancelAccount.title' />}
    >
      {cancelLogs ? (
        cancelLogs.map(line => <div key={line}>{line}</div>)
      ) : (
        <>
          <div>
            <Text message='modules.crm.components.account.cancelAccount.description' />
          </div>
          <FormControlLabel
            className='my-3'
            control={<Checkbox
              color='primary'
              checked={blackList}
              onChange={handleCheckbox}
              dataElementLocation={DataElementLocations.BODY}
              dataElementLabel='cst-cancel-account--block-subdomains-checkbox'
              dataElementId='cst-cancel-account--block-subdomains-checkbox'
              />}
            label={<Text message='modules.crm.components.account.cancelAccount.blacklist' />}
            />
          <RefundInformation
            refundServices={refundServices}
            handleSkipId={serviceId => {
                setIds(xor(skipDomainRegistrationFreePlanIds, [serviceId]))
              }}
            skipDomainRegistrationFreePlanIds={skipDomainRegistrationFreePlanIds}
            cancelType={DIRECTLY}
          />
          <TextField
            fullWidth
            className='mt-4'
            multiline
            rows={3}
            variant='outlined'
            label={<Text message='modules.crm.components.account.cancelReason' />}
            value={reason}
            onChange={handleReasonChange}
            dataElementLocation={DataElementLocations.BODY}
            dataElementLabel='cst-cancel-account-reason'
            dataElementId='cst-cancel-account-reason'
            />
        </>
        )}
    </Modal>
  )
}

CancelAccountDialog.propTypes = {
  cancelAccountById: PropTypes.func.isRequired,
  handleDialog: PropTypes.func.isRequired,
  account: PropTypes.object.isRequired,
  pending: PropTypes.bool.isRequired,
  successLogs: PropTypes.string,
  location: PropTypes.object.isRequired
}

export default withRouter(CancelAccountDialog)
